import { Vector3, OrthographicCamera, Quaternion, Spherical, PerspectiveCamera } from 'three';
import { NavigationControlType, NavigationControls } from './Navigation/NavigationControls';
import type { CameraTarget, CameraTargetProps } from '../types';

const normalizePI = (x: number) => {
  const normalX = x % (2 * Math.PI);
  if (normalX > Math.PI) {
    return normalX - 2 * Math.PI;
  }
  if (normalX < -Math.PI) {
    return normalX + 2 * Math.PI;
  }
  return normalX;
};

const lerp = (from: number, to: number, ratio: number) => {
  return from + (to - from) * ratio;
};

// CameraMover moves camera smoothly to the given position and direction (lookAt point).

export class CameraMover {
  private navigationControls: NavigationControls;

  private needToStart = false;

  private isOrbit = true;

  private fromPos = new Vector3();

  private fromTarget = new Vector3();

  private fromDir = new Vector3();

  private toPos = new Vector3();

  private toTarget = new Vector3();

  private toDir = new Vector3();

  private fromZoom: number | undefined;

  private toZoom: number | undefined;

  private fromFov: number | undefined;

  private toFov: number | undefined;

  private transitionTime = 0.5;

  private startTime = 0;

  private isMoving = false;

  private quaternion = new Quaternion();

  private quaternionInverse = new Quaternion();

  private localUp = new Vector3(0, 1, 0);

  private fromSpherical = new Spherical();

  private toSpherical = new Spherical();

  private fromVector3 = new Vector3();

  private toVector3 = new Vector3();

  private curSpherical = new Spherical();

  private curVector3 = new Vector3();

  private curTarget = new Vector3();

  private invalidateFrame: (frames?: number | undefined) => void;

  private targetsQueue: CameraTarget[] = [];

  constructor(
    navigationControls: NavigationControls,
    invalidateFrame: (frames?: number | undefined) => void
  ) {
    this.navigationControls = navigationControls;
    this.invalidateFrame = invalidateFrame;
  }

  moveCameraToTargetProps(props: CameraTargetProps) {
    const lookAt = new Vector3(...props.lookAt);
    const position = props.position ? new Vector3(...props.position) : undefined;

    this.moveCameraTo({ ...props, position, lookAt });
  }

  moveCameraTo(cameraTarget: CameraTarget) {
    // console.log('moveCameraTo', { ...cameraTarget });
    this.targetsQueue.push(cameraTarget);
    if (this.targetsQueue.length === 1) {
      this.initNextTransition();
    }
  }

  initNextTransition() {
    if (this.targetsQueue.length === 0) {
      return;
    }
    const { position, lookAt, zoom, fov, transitionTime } = this.targetsQueue[0];

    const { camera, controlType } = this.navigationControls;
    if (controlType === NavigationControlType.LookAround) {
      this.isOrbit = false;
      this.fromPos.copy(camera.position);
      this.fromTarget.copy(this.navigationControls.getTarget());
      this.fromDir.copy(this.fromTarget).sub(this.fromPos);

      this.toPos.copy(position ?? this.fromPos);
      this.toTarget.copy(lookAt ?? this.fromTarget);
      this.toDir.copy(this.toTarget).sub(this.toPos);
    } else {
      this.isOrbit = true;
      this.fromPos.copy(camera.position);
      this.fromTarget.copy(this.navigationControls.getTarget());
      this.fromDir.copy(this.fromPos).sub(this.fromTarget);

      this.toPos.copy(position ?? this.fromPos);
      this.toTarget.copy(lookAt ?? this.fromTarget);
      this.toDir.copy(this.toPos).sub(this.toTarget);
    }

    if (camera instanceof OrthographicCamera && zoom) {
      this.fromZoom = camera.zoom;
      this.toZoom = zoom;
    } else {
      this.fromZoom = undefined;
      this.toZoom = undefined;
    }

    if (camera instanceof PerspectiveCamera && fov) {
      this.fromFov = camera.fov;
      this.toFov = fov;
    } else {
      this.fromFov = undefined;
      this.toFov = undefined;
    }

    this.transitionTime = transitionTime ?? 0.5;

    // assuming camera up is [0,0,1]
    // keep camera position slightly away from z axis and keep it on same theta,
    // so that OrbitControls can calculate camera rotation
    const zeroLimit = 0.00001;
    if (Math.abs(this.toDir.x) < zeroLimit && Math.abs(this.toDir.y) < zeroLimit) {
      if (Math.abs(this.fromDir.x) > Math.abs(this.fromDir.y)) {
        this.toDir.x = zeroLimit * Math.sign(this.fromDir.x);
        this.toDir.y = (this.fromDir.y / this.fromDir.x) * this.toDir.x;
      } else if (this.fromDir.y !== 0) {
        this.toDir.y = zeroLimit * Math.sign(this.fromDir.y);
        this.toDir.x = (this.fromDir.x / this.fromDir.y) * this.toDir.y;
      }
    }

    this.quaternion.setFromUnitVectors(camera.up, this.localUp);
    this.quaternionInverse = this.quaternion.clone().invert();

    // rotate from camera.up to localUp (Y-axis is up)
    this.fromDir.applyQuaternion(this.quaternion);
    this.fromSpherical.setFromVector3(this.fromDir);
    this.fromVector3.setX(this.fromSpherical.radius);
    this.fromVector3.setY(this.fromSpherical.phi);
    this.fromVector3.setZ(0);

    this.toDir.applyQuaternion(this.quaternion);
    this.toSpherical.setFromVector3(this.toDir);
    this.toVector3.setX(this.toSpherical.radius);

    // spherical phi range is [0, PI]
    this.toVector3.setY(this.toSpherical.phi);

    // spherical theta range is [-PI, PI]
    // using normalized difference, it's a shortest path direction
    this.toVector3.setZ(normalizePI(this.toSpherical.theta - this.fromSpherical.theta));

    // console.log({
    //   phiFrom: Math.round((this.fromSpherical.phi / Math.PI) * 100) / 100,
    //   phiTo: Math.round((this.toSpherical.phi / Math.PI) * 100) / 100,
    //   thetaFrom: Math.round((this.fromSpherical.theta / Math.PI) * 100) / 100,
    //   thetaTo: Math.round((this.toSpherical.theta / Math.PI) * 100) / 100,
    // });
    this.invalidateFrame();
    this.needToStart = true;
  }

  update(now: number) {
    const { camera } = this.navigationControls;

    if (this.needToStart === true) {
      this.invalidateFrame();
      this.startTime = now;
      this.isMoving = true;
      this.navigationControls.setIsEnabled(false);

      this.needToStart = false;
      return;
    }

    if (this.isMoving === true) {
      this.invalidateFrame();
      const ratio = (now - this.startTime) / this.transitionTime;
      // using cos function to smooth start and stop velocity
      let smoothedRatio = ratio * ((1 - Math.cos(Math.PI * ratio)) / 2);

      if (ratio < 1) {
        this.curVector3.lerpVectors(this.fromVector3, this.toVector3, smoothedRatio);
        if (this.isOrbit) {
          this.curTarget.lerpVectors(this.fromTarget, this.toTarget, smoothedRatio);
        }
      } else {
        smoothedRatio = 1;
        this.curVector3.copy(this.toVector3);
        this.curTarget.copy(this.toTarget);
        this.isMoving = false;
      }

      const radius = this.curVector3.x;
      const phi = this.curVector3.y;
      const theta = normalizePI(this.fromSpherical.theta + this.curVector3.z);
      this.curSpherical.set(radius, phi, theta);

      if (this.toZoom && this.fromZoom && camera instanceof OrthographicCamera) {
        camera.zoom = lerp(this.fromZoom, this.toZoom, smoothedRatio);
        camera.updateProjectionMatrix();
      }
      if (this.fromFov && this.toFov && camera instanceof PerspectiveCamera) {
        camera.fov = lerp(this.fromFov, this.toFov, smoothedRatio);
        camera.updateProjectionMatrix();
      }

      if (this.isOrbit) {
        camera.position.setFromSpherical(this.curSpherical);
        // rotate from localUp to camera.up
        camera.position.applyQuaternion(this.quaternionInverse);
        camera.position.add(this.curTarget);
      } else {
        this.curTarget.setFromSpherical(this.curSpherical);
        // rotate from localUp to camera.up
        this.curTarget.applyQuaternion(this.quaternionInverse);
        this.curTarget.add(camera.position);
      }
      this.navigationControls.lookAt(this.curTarget);

      if (this.isMoving === false) {
        const completedTarget: CameraTarget = this.targetsQueue.shift()!;

        if (this.targetsQueue.length === 0) {
          this.navigationControls.setIsEnabled(true);
        }

        const { onComplete } = completedTarget;
        if (onComplete) {
          onComplete();
        }

        this.initNextTransition();
      }
    }
  }
}
